import React, { useState } from 'react';
import './AddUserToGroupModal.css';

/**
 * Add User to Group Modal Component
 * Allows adding a selected user to a group
 */
const AddUserToGroupModal = ({ show, onHide, user, groups, onSubmit, loading }) => {
  const [selectedGroup, setSelectedGroup] = useState('');

  // Extract user information from LDAP attributes
  const getUserInfo = (user) => {
    if (!user || !user.attributes) return null;
    
    const attributes = user.attributes;
    return {
      uid: attributes.uid?.[0] || 'N/A',
      email: attributes.mail?.[0] || 'N/A',
      fullName: attributes.cn?.[0] || 'N/A'
    };
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!selectedGroup || !user) {
      return;
    }
    
    const userInfo = getUserInfo(user);
    if (!userInfo) {
      return;
    }
    
    await onSubmit(userInfo.uid, selectedGroup);
    
    // Reset form
    setSelectedGroup('');
  };

  // Reset form when modal is closed
  const handleClose = () => {
    setSelectedGroup('');
    onHide();
  };

  if (!show || !user) return null;

  const userInfo = getUserInfo(user);
  if (!userInfo) return null;

  return (
    <div className="modal-overlay" onClick={handleClose}>
      <div className="modal-dialog" onClick={(e) => e.stopPropagation()}>
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">Add User to Group</h5>
            <button 
              type="button" 
              className="btn-close" 
              onClick={handleClose}
              disabled={loading}
            >
              <span>&times;</span>
            </button>
          </div>
          
          <form onSubmit={handleSubmit}>
            <div className="modal-body">
              {/* User Information */}
              <div className="user-info-section">
                <h6>User Information:</h6>
                <div className="user-info-card">
                  <div className="user-detail">
                    <strong>Name:</strong> {userInfo.fullName}
                  </div>
                  <div className="user-detail">
                    <strong>User ID:</strong> 
                    <span className="uid-badge">{userInfo.uid}</span>
                  </div>
                  <div className="user-detail">
                    <strong>Email:</strong> 
                    <a href={`mailto:${userInfo.email}`} className="email-link">
                      {userInfo.email}
                    </a>
                  </div>
                </div>
              </div>

              {/* Group Selection */}
              <div className="group-selection-section">
                <div className="form-group">
                  <label htmlFor="groupSelect" className="form-label">
                    Select Group <span className="required">*</span>
                  </label>
                  <select
                    id="groupSelect"
                    className="form-select"
                    value={selectedGroup}
                    onChange={(e) => setSelectedGroup(e.target.value)}
                    disabled={loading}
                    required
                  >
                    <option value="">Choose a group...</option>
                    {groups.map((group, index) => (
                      <option key={index} value={group.name}>
                        {group.name}
                        {group.description && ` (${group.description})`}
                      </option>
                    ))}
                  </select>
                </div>

                {groups.length === 0 && (
                  <div className="no-groups-message">
                    <p className="text-muted">
                      <i className="fas fa-exclamation-triangle"></i>
                      No groups available. Please create a group first.
                    </p>
                  </div>
                )}
              </div>
            </div>
            
            <div className="modal-footer">
              <button 
                type="button" 
                className="btn btn-secondary" 
                onClick={handleClose}
                disabled={loading}
              >
                Cancel
              </button>
              <button 
                type="submit" 
                className="btn btn-primary"
                disabled={loading || !selectedGroup || groups.length === 0}
              >
                {loading ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2"></span>
                    Adding...
                  </>
                ) : (
                  <>
                    <i className="fas fa-user-plus me-2"></i>
                    Add to Group
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default AddUserToGroupModal;
